import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  Activity,
  Briefcase,
  CheckCircle2,
  Columns3,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Eye,
  Mail,
  Phone,
  Plus as PlusIcon,
  RefreshCw,
  Search,
  UserRound,
  Users,
  WalletCards
} from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';

const labelRighePerPagina = 'Righe per pagina:';

const labelDaA = ({ from, to, count }) => {
  const total = count !== -1 ? count : `più di ${to}`;
  return `${from}-${to} di ${total}`;
};

function formatCurrency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
}

function formatPercent(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `${amount.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('it-IT');
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function normalizeBadgeColor(value) {
  return String(value || '').trim().toLowerCase();
}

function isHexColor(value) {
  const s = String(value || '').trim();
  return /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(s);
}

function hexToRgb(hex) {
  const raw = String(hex || '').trim().replace('#', '');
  if (raw.length === 3) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    return { r, g, b };
  }
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return { r, g, b };
}

function rgba(rgb, alpha) {
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function getBadgeStyle(color) {
  const c = normalizeBadgeColor(color);
  if (isHexColor(c)) {
    const rgb = hexToRgb(c);
    return {
      border: rgba(rgb, 0.35),
      bg: rgba(rgb, 0.08),
      fg: c
    };
  }

  const map = {
    blue: { border: 'rgba(59, 130, 246, 0.35)', bg: 'rgba(59, 130, 246, 0.08)', fg: '#2563EB' },
    indigo: { border: 'rgba(99, 102, 241, 0.35)', bg: 'rgba(99, 102, 241, 0.08)', fg: '#4F46E5' },
    amber: { border: 'rgba(245, 158, 11, 0.35)', bg: 'rgba(245, 158, 11, 0.08)', fg: '#D97706' },
    green: { border: 'rgba(16, 185, 129, 0.35)', bg: 'rgba(16, 185, 129, 0.08)', fg: '#059669' },
    teal: { border: 'rgba(20, 184, 166, 0.35)', bg: 'rgba(20, 184, 166, 0.08)', fg: '#0D9488' },
    red: { border: 'rgba(239, 68, 68, 0.35)', bg: 'rgba(239, 68, 68, 0.08)', fg: '#DC2626' },
    gray: { border: 'rgba(100, 116, 139, 0.35)', bg: 'rgba(100, 116, 139, 0.08)', fg: '#475569' },
    grey: { border: 'rgba(100, 116, 139, 0.35)', bg: 'rgba(100, 116, 139, 0.08)', fg: '#475569' }
  };

  return map[c] || map.gray;
}

function getScadenzaStyle(row) {
  if (!row?.data_scadenza || Number(row?.stato_finale)) {
    return { color: 'text.primary', fontWeight: 400 };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadline = new Date(row.data_scadenza);
  if (Number.isNaN(deadline.getTime())) {
    return { color: 'text.primary', fontWeight: 400 };
  }
  deadline.setHours(0, 0, 0, 0);

  if (deadline < today) {
    return { color: '#DC2626', fontWeight: 700 };
  }

  const diffDays = Math.ceil((deadline.getTime() - today.getTime()) / 86400000);
  if (diffDays <= 7) {
    return { color: '#D97706', fontWeight: 700 };
  }

  return { color: 'text.primary', fontWeight: 400 };
}

const StatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }} elevation={0} variant="outlined">
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const ClientCell = ({ row }) => {
  const name = String(row.cliente_label || '').trim() || 'Cliente non disponibile';
  const phone = String(row.cliente_telefono || '').trim();
  const email = String(row.cliente_email || '').trim();

  return (
    <Box sx={{ minWidth: 220, display: 'grid', gap: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <UserRound size={14} color="#2563eb" />
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {name}
        </Typography>
      </Box>
      {phone ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Phone size={13} color="#64748b" />
          <Typography variant="caption" color="text.secondary">
            {phone}
          </Typography>
        </Box>
      ) : null}
      {email ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Mail size={13} color="#64748b" />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {email}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default function PfPratiche() {
  const navigate = useNavigate();
  const columnDefs = useMemo(
    () => [
      { key: 'id', label: 'ID', sortable: true, align: 'left' },
      { key: 'cliente_label', label: 'Cliente', sortable: true, align: 'left' },
      { key: 'tipo_operazione_nome', label: 'Tipo operazione', sortable: true, align: 'left' },
      { key: 'stato_nome_visualizzato', label: 'Stato', sortable: true, align: 'left' },
      { key: 'importo_richiesto', label: 'Richiesto', sortable: true, align: 'right' },
      { key: 'importo_finanziabile', label: 'Finanziabile', sortable: true, align: 'right' },
      { key: 'importo_erogato', label: 'Erogato', sortable: true, align: 'right' },
      { key: 'importo_rata', label: 'Rata', sortable: true, align: 'right' },
      { key: 'numero_rate', label: 'N. rate', sortable: true, align: 'right' },
      { key: 'tan', label: 'TAN', sortable: true, align: 'right' },
      { key: 'taeg', label: 'TAEG', sortable: true, align: 'right' },
      { key: 'istituto_erogante_nome', label: 'Istituto', sortable: true, align: 'left' },
      { key: 'data_erogazione', label: 'Data erogazione', sortable: true, align: 'left' },
      { key: 'data_scadenza', label: 'Scadenza', sortable: true, align: 'left' },
      { key: 'data_recall', label: 'Recall', sortable: true, align: 'left' },
      { key: 'operatore_nome', label: 'Operatore', sortable: true, align: 'left' },
      { key: 'data_inserimento', label: 'Inserita il', sortable: true, align: 'left' }
    ],
    []
  );

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('data_inserimento');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    stato_id: '',
    tipo_operazione_id: '',
    istituto_erogante_id: '',
    finale: ''
  });
  const [stats, setStats] = useState({
    totale: 0,
    aperte: 0,
    chiuse: 0,
    importo_erogato_totale: 0,
    importo_richiesto_totale: 0,
    scadute: 0
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'cliente_label',
    'tipo_operazione_nome',
    'stato_nome_visualizzato',
    'importo_richiesto',
    'importo_erogato',
    'importo_rata',
    'numero_rate',
    'istituto_erogante_nome',
    'data_scadenza'
  ]);

  const [lookups, setLookups] = useState({
    clienti: [],
    tipiOperazione: [],
    stati: [],
    istituti: []
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const displayedColumns = useMemo(
    () => columnDefs.filter((col) => visibleColumns.includes(col.key)),
    [columnDefs, visibleColumns]
  );

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  const buildQueryParams = () => ({
    page: page + 1,
    limit: rowsPerPage,
    orderBy,
    orderDirection,
    search: debouncedSearch || undefined,
    stato_id: filters.stato_id || undefined,
    tipo_operazione_id: filters.tipo_operazione_id || undefined,
    istituto_erogante_id: filters.istituto_erogante_id || undefined,
    finale: filters.finale !== '' ? filters.finale : undefined
  });

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await api.get('/pf-pratiche', { params: buildQueryParams() });
      const payload = response.data || {};
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotal(Number(payload.total || 0));
      setError('');
    } catch (err) {
      console.error('Error fetching pf pratiche:', err);
      setError('Errore nel caricamento delle pratiche');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await api.get('/pf-pratiche/stats', {
        params: {
          search: debouncedSearch || undefined,
          stato_id: filters.stato_id || undefined,
          tipo_operazione_id: filters.tipo_operazione_id || undefined,
          istituto_erogante_id: filters.istituto_erogante_id || undefined,
          finale: filters.finale !== '' ? filters.finale : undefined
        }
      });
      setStats({
        totale: Number(response.data?.totale || 0),
        aperte: Number(response.data?.aperte || 0),
        chiuse: Number(response.data?.chiuse || 0),
        importo_erogato_totale: Number(response.data?.importo_erogato_totale || 0),
        importo_richiesto_totale: Number(response.data?.importo_richiesto_totale || 0),
        scadute: Number(response.data?.scadute || 0)
      });
    } catch (err) {
      console.error('Error fetching pf pratiche stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchLookups = async () => {
    try {
      const [clientiRes, tipiRes, statiRes, istitutiRes] = await Promise.all([
        api.get('/pf-clienti', {
          params: {
            page: 1,
            limit: 200,
            orderBy: 'cognome',
            orderDirection: 'asc'
          }
        }),
        api.get('/pf-tipi-operazione'),
        api.get('/pf-stati-pratica'),
        api.get('/pf-istituti-eroganti')
      ]);

      setLookups({
        clienti: Array.isArray(clientiRes.data?.data) ? clientiRes.data.data : [],
        tipiOperazione: Array.isArray(tipiRes.data) ? tipiRes.data : [],
        stati: Array.isArray(statiRes.data) ? statiRes.data : [],
        istituti: Array.isArray(istitutiRes.data) ? istitutiRes.data : []
      });
    } catch (err) {
      console.error('Error fetching pf pratiche lookups:', err);
      toast.error('Errore nel caricamento dei dati di supporto');
    }
  };

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    fetchRows();
    fetchStats();
  }, [page, rowsPerPage, orderBy, orderDirection, debouncedSearch, filters]);

  const handleRefresh = async () => {
    await Promise.all([fetchRows(), fetchStats(), fetchLookups()]);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
    setPage(0);
  };

  const handleToggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((current) => current !== key);
      }
      return [...prev, key];
    });
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/pf-pratiche/${deleteTarget.id}`);
      toast.success('Pratica eliminata con successo');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await Promise.all([fetchRows(), fetchStats()]);
    } catch (err) {
      console.error('Error deleting pf pratica:', err);
      const message = err?.response?.data?.message || 'Errore durante eliminazione';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderCellValue = (row, key) => {
    if (key === 'cliente_label') {
      return <ClientCell row={row} />;
    }

    if (key === 'stato_nome_visualizzato') {
      const badge = getBadgeStyle(row.stato_colore_badge);
      return (
        <Chip
          label={row.stato_nome_visualizzato || '-'}
          size="small"
          variant="outlined"
          sx={{
            borderColor: badge.border,
            bgcolor: badge.bg,
            color: badge.fg,
            fontWeight: 600
          }}
        />
      );
    }

    if (['importo_richiesto', 'importo_finanziabile', 'importo_erogato', 'importo_rata'].includes(key)) {
      return formatCurrency(row[key]);
    }

    if (['tan', 'taeg'].includes(key)) {
      return formatPercent(row[key]);
    }

    if (key === 'numero_rate') {
      return row.numero_rate ? `${row.numero_rate}` : '-';
    }

    if (key === 'data_erogazione' || key === 'data_recall') {
      return formatDate(row[key]);
    }

    if (key === 'data_scadenza') {
      return (
        <Box component="span" sx={getScadenzaStyle(row)}>
          {formatDate(row.data_scadenza)}
        </Box>
      );
    }

    if (key === 'data_inserimento') {
      return formatDateTime(row.data_inserimento);
    }

    return row[key] ?? '-';
  };

  return (
    <Box className="p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <Briefcase className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Pratiche Finanziarie — Pratiche
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Gestione operativa delle pratiche con workflow, importi e scadenze
            </Typography>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} title="Ricarica">
            <RefreshCw className="mr-2 h-4 w-4" />
            Sincronizza
          </Button>
          <Button onClick={() => navigate('/pratiche-finanziarie/pratiche/nuova')}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuova pratica
          </Button>
        </div>
      </div>

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Totale pratiche"
            value={statsLoading ? '...' : stats.totale}
            icon={WalletCards}
            color="#6366f1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Pratiche aperte"
            value={statsLoading ? '...' : stats.aperte}
            icon={Activity}
            color="#0ea5e9"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Pratiche chiuse"
            value={statsLoading ? '...' : stats.chiuse}
            icon={CheckCircle2}
            color="#22c55e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Importo erogato totale"
            value={statsLoading ? '...' : formatCurrency(stats.importo_erogato_totale)}
            icon={Users}
            color="#f59e0b"
          />
        </Grid>
      </Grid>

      <Card className="overflow-hidden rounded-2xl border-none shadow-sm">
        <Box
          sx={{
            p: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr 1fr 1fr 1fr auto' },
            gap: 1.5,
            alignItems: 'center'
          }}
        >
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Cerca per ID, cliente, codice fiscale, stato, istituto..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
          />

          <FormControl size="small">
            <InputLabel>Stato</InputLabel>
            <Select
              label="Stato"
              value={filters.stato_id}
              onChange={(event) => handleFilterChange('stato_id', event.target.value)}
            >
              <MenuItem value="">Tutti gli stati</MenuItem>
              {lookups.stati.map((row) => (
                <MenuItem key={row.id} value={String(row.id)}>
                  {row.nome_visualizzato}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={filters.tipo_operazione_id}
              onChange={(event) => handleFilterChange('tipo_operazione_id', event.target.value)}
            >
              <MenuItem value="">Tutti i tipi</MenuItem>
              {lookups.tipiOperazione.map((row) => (
                <MenuItem key={row.id} value={String(row.id)}>
                  {row.nome_visualizzato}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Istituto</InputLabel>
            <Select
              label="Istituto"
              value={filters.istituto_erogante_id}
              onChange={(event) => handleFilterChange('istituto_erogante_id', event.target.value)}
            >
              <MenuItem value="">Tutti gli istituti</MenuItem>
              {lookups.istituti.map((row) => (
                <MenuItem key={row.id} value={String(row.id)}>
                  {row.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Esito</InputLabel>
            <Select
              label="Esito"
              value={filters.finale}
              onChange={(event) => handleFilterChange('finale', event.target.value)}
            >
              <MenuItem value="">Tutte</MenuItem>
              <MenuItem value="0">Aperte</MenuItem>
              <MenuItem value="1">Chiuse</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Colonne">
            <IconButton onClick={(event) => setColumnsMenuAnchor(event.currentTarget)}>
              <Columns3 size={18} />
            </IconButton>
          </Tooltip>
        </Box>

        <Menu anchorEl={columnsMenuAnchor} open={Boolean(columnsMenuAnchor)} onClose={() => setColumnsMenuAnchor(null)}>
          {columnDefs.map((col) => {
            const checked = visibleColumns.includes(col.key);
            const disableHide = checked && visibleColumns.length === 1;
            return (
              <MenuItem key={col.key} dense onClick={() => handleToggleColumn(col.key)} disabled={disableHide}>
                <Checkbox size="small" checked={checked} />
                <ListItemText primary={col.label} />
              </MenuItem>
            );
          })}
        </Menu>

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                {displayedColumns.map((col) => (
                  <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap', minWidth: col.key === 'cliente_label' ? 220 : undefined }}>
                    {col.sortable ? (
                      <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? orderDirection : 'asc'}
                        onClick={() => handleRequestSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={displayedColumns.length + 1} align="center" className="py-8">
                    <RefreshCw className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={displayedColumns.length + 1} align="center" className="py-8 text-gray-500">
                    Nessuna pratica trovata
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    {displayedColumns.map((col) => (
                      <TableCell key={`${row.id}-${col.key}`} align={col.align} sx={{ minWidth: col.key === 'cliente_label' ? 220 : undefined }}>
                        {renderCellValue(row, col.key)}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Apri dettaglio">
                          <IconButton size="small" onClick={() => navigate(`/pratiche-finanziarie/pratiche/${row.id}`)}>
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifica">
                          <IconButton size="small" onClick={() => navigate(`/pratiche-finanziarie/pratiche/${row.id}/modifica`)}>
                            <EditIcon size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Elimina">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                            <DeleteIcon size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={labelRighePerPagina}
          labelDisplayedRows={labelDaA}
        />
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Conferma eliminazione pratica
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Stai per eliminare in modo definitivo la pratica {deleteTarget ? `#${deleteTarget.id}` : ''}.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Verifica i dati riepilogativi qui sotto prima di procedere. L&apos;operazione non puó essere annullata.
              </Typography>
            </Box>

            <Box
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                p: 2,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.5
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35 }}>
                  Cliente
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {deleteTarget?.cliente_label || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35 }}>
                  Tipo operazione
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {deleteTarget?.tipo_operazione_nome || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35 }}>
                  Stato
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {deleteTarget?.stato_nome_visualizzato || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35 }}>
                  Importo richiesto
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(deleteTarget?.importo_richiesto)}
                </Typography>
              </Box>
            </Box>

            <Alert severity="warning" sx={{ alignItems: 'flex-start' }}>
              L&apos;eliminazione rimuove la pratica dall&apos;elenco e dalle statistiche operative di Pratiche Finanziarie.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outline"
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
          >
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Conferma eliminazione'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
